/**
 * Google Analytics 4 (GA4) утилиттері
 *
 * Қолдану:
 * 1. public/index.html -ға GA4 тегін қосыңыз
 * 2. Осы файлдан initAnalytics() шақырыңыз
 * 3. trackPageView, trackEvent функцияларын қолданыңыз
 */

// GA4 Measurement ID (мұны нақты ID-ге ауыстырыңыз)
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-XXXXXXXXXX';

/**
 * Google Analytics скриптін жүктеу
 */
export function loadGoogleAnalytics() {
  // Екі рет жүктелмеуін тексеру
  if (window.gtag) {
    console.log('[GA] Analytics already loaded');
    return;
  }

  // gtag.js скриптін жүктеу
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // gtag конфигурациясы
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // GA4 конфигурациясы
  script.onload = () => {
    window.gtag('js', new Date());
    window.gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: false, // Қолмен page_view жібереміз
      anonymize_ip: true, // IP мекенжайларын анонимизациялау
    });
    console.log('[GA] Google Analytics loaded successfully');
  };

  script.onerror = () => {
    console.error('[GA] Failed to load Google Analytics');
  };
}

/**
 * Бетті қарауды бақылау
 * @param {string} pagePath - Беттің жолы (мысалы: '/category/technikа')
 * @param {Object} pageProps - Қосымша қасиеттер
 */
export function trackPageView(pagePath, pageProps = {}) {
  if (!window.gtag) {
    console.warn('[GA] gtag not available, skipping page view tracking');
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: document.title,
    page_location: window.location.href,
    ...pageProps,
  });
}

/**
 * Оқиғаны бақылау
 * @param {string} eventName - Оқиға атауы
 * @param {Object} eventParams - Оқиға параметрлері
 */
export function trackEvent(eventName, eventParams = {}) {
  if (!window.gtag) {
    console.warn('[GA] gtag not available, skipping event tracking');
    return;
  }

  window.gtag('event', eventName, eventParams);
}

/**
 * Электрондық коммерция оқиғалары
 */

/**
 * Өнімді қарауды бақылау
 * @param {Object} product - Өнім деректері
 */
export function trackProductView(product) {
  trackEvent('view_item', {
    currency: 'KZT',
    value: product.price,
    items: [{
      item_id: product.id,
      item_name: product.title,
      item_category: product.category,
      price: product.price,
      quantity: 1,
    }],
  });
}

/**
 * Себетке қосуды бақылау
 * @param {Object} product - Өнім деректері
 * @param {number} quantity - Саны
 */
export function trackAddToBasket(product, quantity = 1) {
  trackEvent('add_to_basket', {
    currency: 'KZT',
    value: product.price * quantity,
    items: [{
      item_id: product.id,
      item_name: product.title,
      item_category: product.category,
      price: product.price,
      quantity: quantity,
    }],
  });
}

/**
 * Іздеуді бақылау
 * @param {string} searchTerm - Іздеу сөзі
 * @param {string} category - Категория (қосымша)
 */
export function trackSearch(searchTerm, category = null) {
  trackEvent('search', {
    search_term: searchTerm,
    item_category: category,
  });
}

/**
 * Байланыс әрекеттерін бақылау
 * @param {string} method - Байланыс әдісі (phone, whatsapp, email)
 * @param {string} phoneNumber - Телефон нөмірі
 */
export function trackContact(method, phoneNumber = null) {
  trackEvent('contact', {
    contact_method: method,
    phone_number: phoneNumber,
  });
}

/**
 * Форманы жіберуді бақылау
 * @param {string} formName - Форманың атауы
 * @param {Object} formData - Форма деректері
 */
export function trackFormSubmit(formName, formData = {}) {
  trackEvent('form_submit', {
    form_name: formName,
    ...formData,
  });
}

/**
 * Video көруді бақылау
 * @param {string} videoTitle - Видео атауы
 * @param {string} provider - Видео провайдері (youtube, vimeo, etc.)
 */
export function trackVideoPlay(videoTitle, provider = 'custom') {
  trackEvent('video_start', {
    video_title: videoTitle,
    video_provider: provider,
  });
}

/**
 * Файлды жүктеуді бақылау
 * @param {string} fileName - Файл атауы
 * @param {string} fileType - Файл түрі (pdf, doc, xls)
 */
export function trackFileDownload(fileName, fileType) {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
}

/**
 * Қате оқиғасын бақылау
 * @param {string} errorDescription - Қате сипаттамасы
 * @param {string} errorFatal - Қате маңыздылығы (true/false)
 */
export function trackError(errorDescription, errorFatal = false) {
  trackEvent('exception', {
    description: errorDescription,
    fatal: errorFatal,
  });
}

const googleAnalytics = {
  loadGoogleAnalytics,
  trackPageView,
  trackEvent,
  trackProductView,
  trackAddToBasket,
  trackSearch,
  trackContact,
  trackFormSubmit,
  trackVideoPlay,
  trackFileDownload,
  trackError,
};

export default googleAnalytics;